import { Suspense, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { PdpaProvider, usePdpa } from './context/PdpaContext';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import Toast from './components/common/Toast';
import ConfirmDialog from './components/common/ConfirmDialog';
import LoginScreen from './components/auth/LoginScreen';
import ResetPasswordScreen from './components/auth/ResetPasswordScreen';
import { lazyRetry } from './lib/staleChunkReload';

const Dashboard = lazyRetry(() => import('./components/pages/Dashboard'));
const Registry = lazyRetry(() => import('./components/pages/Registry'));
const RegisterForm = lazyRetry(() => import('./components/pages/RegisterForm'));
const ImportSystems = lazyRetry(() => import('./components/pages/ImportSystems'));
const Onboard = lazyRetry(() => import('./components/pages/Onboard'));
const Offboard = lazyRetry(() => import('./components/pages/Offboard'));
const AccessRequest = lazyRetry(() => import('./components/pages/AccessRequest'));
const PermissionManagement = lazyRetry(() => import('./components/pages/PermissionManagement'));
const PrivacySettings = lazyRetry(() => import('./components/pages/PrivacySettings'));
const SmtpSettings = lazyRetry(() => import('./components/pages/SmtpSettings'));
const ChecklistSettings = lazyRetry(() => import('./components/pages/ChecklistSettings'));
const ExitInterviewSettings = lazyRetry(() => import('./components/pages/ExitInterviewSettings'));
const OffboardPublicAction = lazyRetry(() => import('./components/pages/OffboardPublicAction'));

function getOffboardActionToken() {
  const path = window.location.pathname || '';
  const match = path.match(/^\/offboard\/action\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function PageFallback() {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text2)' }}>
      <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }} />
      กำลังโหลดหน้า...
    </div>
  );
}

function AuthenticatedApp() {
  const {
    closeSidebar,
    currentUser,
    isAuthenticated,
    logout,
    showToast,
    sidebarOpen,
    sidebarCollapsed,
    toggleSidebar,
    toggleSidebarCollapsed,
    apiFetch,
  } = useApp();

  return (
    <PdpaProvider
      apiFetch={apiFetch}
      currentUser={currentUser}
      isAuthenticated={isAuthenticated}
      logout={logout}
      showToast={showToast}
    >
      <div className={`app${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? <i className="fa-solid fa-xmark"></i> : <i className="fa-solid fa-bars"></i>}
        </button>
        {sidebarCollapsed && (
          <button type="button" className="sidebar-expand-btn" onClick={toggleSidebarCollapsed} title="เปิดเมนู" aria-label="เปิดเมนู">
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        )}
        <div className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />
        <Sidebar />
        <div className="main">
          <Topbar />
          <div className="content">
            <AuthenticatedPage />
          </div>
        </div>
        <Toast />
        <ConfirmDialog />
      </div>
    </PdpaProvider>
  );
}

function AuthenticatedPage() {
  const { activePage, showPage } = useApp();
  const { isPdpaEnabled, pdpaCheckComplete } = usePdpa();

  useEffect(() => {
    if (activePage === 'privacy-settings' && pdpaCheckComplete && !isPdpaEnabled) {
      showPage('dashboard');
    }
  }, [activePage, isPdpaEnabled, pdpaCheckComplete, showPage]);

  let page;
  switch (activePage) {
    case 'dashboard': page = <Dashboard />; break;
    case 'registry': page = <Registry />; break;
    case 'register':
    case 'register-form': page = <RegisterForm />; break;
    case 'import-systems': page = <ImportSystems />; break;
    case 'onboard': page = <Onboard />; break;
    case 'offboard': page = <Offboard />; break;
    case 'access-request': page = <AccessRequest />; break;
    case 'permissions': page = <PermissionManagement />; break;
    case 'smtp-settings': page = <SmtpSettings />; break;
    case 'checklist-settings': page = <ChecklistSettings />; break;
    case 'exit-interview-settings': page = <ExitInterviewSettings />; break;
    case 'privacy-settings': page = <PrivacySettings />; break;
    default: page = <Dashboard />; break;
  }

  return <Suspense fallback={<PageFallback />}>{page}</Suspense>;
}

function AppContent() {
  const {
    authStatus,
    authView,
    goToLogin,
    handleResetPassword,
    isAuthenticated,
    login,
    loginWithMicrosoft,
    resetPasswordUsername,
  } = useApp();

  if (authStatus === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--surface)' }}>
        <div className="form-card" style={{ maxWidth: '420px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink)' }}>กำลังตรวจสอบ session</div>
          <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: '8px' }}>
            กำลังโหลดข้อมูลผู้ใช้จาก backend local
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authView === 'resetPassword') {
      return (
        <>
          <ResetPasswordScreen
            username={resetPasswordUsername}
            onSubmit={handleResetPassword}
            onBack={goToLogin}
            loading={authStatus === 'loading'}
          />
          <Toast />
          <ConfirmDialog />
        </>
      );
    }

    return (
      <>
        <LoginScreen onSubmit={login} onMicrosoftLogin={loginWithMicrosoft} loading={authStatus === 'loading'} />
        <Toast />
        <ConfirmDialog />
      </>
    );
  }

  return <AuthenticatedApp />;
}

export default function App() {
  const publicToken = getOffboardActionToken();
  if (publicToken) {
    return (
      <Suspense fallback={<PageFallback />}>
        <OffboardPublicAction token={publicToken} />
      </Suspense>
    );
  }

  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
